import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Alert,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Tooltip,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  VideoFile as VideoFileIcon,
  Delete as DeleteIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { DropZoneProps, BatchDropZoneProps, FileWithStatus, FileStatus, AdMetadata } from '../../types';
import { DROPZONE_ACCEPT, SUPPORTED_UPLOAD_EXTENSIONS, isHtmlUploadFile, validateFile } from '../../services/upload';

interface FileWithValidation extends File {
  id: string;
  validation?: {
    isValid: boolean;
    error?: string;
  };
  preview?: {
    duration?: number;
    width?: number;
    height?: number;
  };
}

// Original DropZone component (unchanged for backward compatibility)
const DropZone: React.FC<DropZoneProps> = ({
  onFilesSelected,
  accept = Array.from(SUPPORTED_UPLOAD_EXTENSIONS),
  maxSize = 2 * 1024 * 1024 * 1024, // 2GB
  multiple = true,
  disabled = false,
}) => {
  const [selectedFiles, setSelectedFiles] = useState<FileWithValidation[]>([]);
  const [error, setError] = useState<string | null>(null);

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: any[]) => {
      setError(null);

      // Handle rejected files
      if (rejectedFiles.length > 0) {
        const rejection = rejectedFiles[0];
        if (rejection.errors?.[0]?.code === 'file-too-large') {
          setError(`File too large. Maximum size is ${formatFileSize(maxSize)}`);
        } else if (rejection.errors?.[0]?.code === 'file-invalid-type') {
          setError(`Invalid file type. Accepted types: ${accept.join(', ')}`);
        } else {
          setError('Some files were rejected');
        }
        return;
      }

      // Validate and process accepted files
      const validatedFiles: FileWithValidation[] = acceptedFiles.map((file) => {
        // Create safe ID - handle cases where file.name might be undefined
        const fileName = file.name || 'unnamed_file';
        const fileWithId = Object.assign(file, {
          id: `${fileName}-${file.lastModified || Date.now()}-${Math.random()}`,
        }) as FileWithValidation;

        // Validate file
        const validation = validateFile(file);
        fileWithId.validation = validation;

        return fileWithId;
      });

      // Update selected files
      const newFiles = multiple 
        ? [...selectedFiles, ...validatedFiles]
        : validatedFiles;

      setSelectedFiles(newFiles);

      // Extract valid files and call callback
      const validFiles = newFiles.filter((f: FileWithValidation) => f.validation?.isValid).map((f: FileWithValidation) => f as File);
      onFilesSelected(validFiles);
    },
    [selectedFiles, multiple, onFilesSelected, accept, maxSize]
  );

  const removeFile = useCallback(
    (fileId: string) => {
      const newFiles = selectedFiles.filter(f => f.id !== fileId);
      setSelectedFiles(newFiles);
      
      // Update callback with valid files
      const validFiles = newFiles.filter((f: FileWithValidation) => f.validation?.isValid).map((f: FileWithValidation) => f as File);
      onFilesSelected(validFiles);
    },
    [selectedFiles, onFilesSelected]
  );

  const clearAll = useCallback(() => {
    setSelectedFiles([]);
    setError(null);
    onFilesSelected([]);
  }, [onFilesSelected]);

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    accept: DROPZONE_ACCEPT,
    maxSize,
    multiple,
    disabled,
  });

  const formatFileSize = (bytes: number): string => {
    // Handle invalid inputs
    if (bytes === null || bytes === undefined || isNaN(bytes) || bytes < 0) {
      return '0 Bytes';
    }
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getDropzoneStyles = () => {
    let borderColor = 'grey.300';
    let backgroundColor = 'background.paper';
    
    if (disabled) {
      borderColor = 'grey.200';
      backgroundColor = 'grey.50';
    } else if (isDragReject) {
      borderColor = 'error.main';
      backgroundColor = 'error.light';
    } else if (isDragActive) {
      borderColor = 'primary.main';
      backgroundColor = 'primary.light';
    }

    return {
      borderColor,
      backgroundColor,
      opacity: disabled ? 0.6 : 1,
    };
  };

  const validFilesCount = selectedFiles.filter((f: FileWithValidation) => f.validation?.isValid).length;
  const invalidFilesCount = selectedFiles.filter((f: FileWithValidation) => !f.validation?.isValid).length;

  return (
    <Box>
      {/* Dropzone Area */}
      <Paper
        {...getRootProps()}
        sx={{
          border: 2,
          borderStyle: 'dashed',
          borderRadius: 2,
          p: 4,
          textAlign: 'center',
          cursor: disabled ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s ease-in-out',
          minHeight: 200,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          ...getDropzoneStyles(),
        }}
      >
        <input {...getInputProps()} />
        
        <CloudUploadIcon 
          sx={{ 
            fontSize: 64, 
            color: disabled ? 'grey.400' : isDragActive ? 'primary.main' : 'grey.500',
            mb: 2 
          }} 
        />
        
        <Typography variant="h6" gutterBottom>
          {isDragActive
            ? 'Drop your files here'
            : disabled
            ? 'Upload disabled'
            : 'Drag & drop videos, images, or HTML files here, or click to select'
          }
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Supported formats: {accept.join(', ')}
        </Typography>
        
        <Typography variant="body2" color="text.secondary">
          Maximum file size: {formatFileSize(maxSize)}
        </Typography>
        
        {!multiple && selectedFiles.length > 0 && (
          <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
            Click to replace current file
          </Typography>
        )}
      </Paper>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Files Summary */}
      {selectedFiles.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Selected Files ({selectedFiles.length})
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {validFilesCount > 0 && (
                <Chip
                  icon={<CheckCircleIcon />}
                  label={`${validFilesCount} valid`}
                  color="success"
                  size="small"
                />
              )}
              {invalidFilesCount > 0 && (
                <Chip
                  icon={<ErrorIcon />}
                  label={`${invalidFilesCount} invalid`}
                  color="error"
                  size="small"
                />
              )}
              <IconButton
                size="small"
                onClick={clearAll}
                color="error"
                title="Clear all files"
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Files List */}
          <Paper sx={{ maxHeight: 300, overflow: 'auto' }}>
            <List dense>
              {selectedFiles.map((file) => {
                const isValid = file.validation?.isValid ?? true;
                const error = file.validation?.error;
                
                return (
                  <ListItem
                    key={file.id}
                    sx={{
                      bgcolor: isValid ? 'transparent' : 'error.light',
                      '&:hover': {
                        bgcolor: isValid ? 'action.hover' : 'error.light',
                      },
                    }}
                  >
                    <ListItemIcon>
                      <VideoFileIcon color={isValid ? 'primary' : 'error'} />
                    </ListItemIcon>
                    
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: isValid ? 'text.primary' : 'error.main',
                              fontWeight: isValid ? 'normal' : 'medium',
                            }}
                          >
                            {file.name}
                          </Typography>
                          {!isValid && (
                            <ErrorIcon color="error" fontSize="small" />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {formatFileSize(file.size)}
                            {file.preview?.duration && !isNaN(file.preview.duration) && (
                              <> • {formatDuration(file.preview.duration)}</>
                            )}
                            {file.preview?.width && file.preview?.height && 
                             !isNaN(file.preview.width) && !isNaN(file.preview.height) && (
                              <> • {file.preview.width}×{file.preview.height}</>
                            )}
                          </Typography>
                          {error && (
                            <Typography variant="caption" color="error.main" display="block">
                              {error}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        onClick={() => removeFile(file.id)}
                        size="small"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                );
              })}
            </List>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

// New BatchDropZone component for batch upload mode
export const BatchDropZone: React.FC<BatchDropZoneProps> = ({
  onFilesChange,
  onFileClick,
  onFileDelete,
  files,
  accept = Array.from(SUPPORTED_UPLOAD_EXTENSIONS),
  maxSize = 2 * 1024 * 1024 * 1024, // 2GB
  disabled = false,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [thumbnails, setThumbnails] = useState<Record<string, string>>({});

  const onDrop = useCallback(
    async (acceptedFiles: File[], rejectedFiles: any[]) => {
      setError(null);

      // Handle rejected files
      if (rejectedFiles.length > 0) {
        const rejection = rejectedFiles[0];
        if (rejection.errors?.[0]?.code === 'file-too-large') {
          setError(`File too large. Maximum size is ${formatFileSize(maxSize)}`);
        } else if (rejection.errors?.[0]?.code === 'file-invalid-type') {
          setError(`Invalid file type. Accepted types: ${accept.join(', ')}`);
        } else {
          setError('Some files were rejected');
        }
        return;
      }

      // Create FileWithStatus objects for new files
      const newFiles: FileWithStatus[] = [];
      
      for (const file of acceptedFiles) {
        // Debug logging for missing file names
        if (!file.name) {
          console.warn('File object missing name property:', {
            size: file.size,
            type: file.type,
            lastModified: file.lastModified,
            fileObject: file
          });
        }
        
        // Debug file size before processing
        console.log('File size before processing:', file.name, file.size);
        
        const validation = validateFile(file);
        // Create safe ID - handle cases where file.name might be undefined
        const fileName = file.name || 'unnamed_file';
        
        // IMPORTANT: Preserve the original File object and just add our custom properties
        // Don't create a new object - that breaks the File prototype chain!
        const fileWithStatus = file as FileWithStatus;
        
        // Add our custom properties to the existing File object
        fileWithStatus.id = `${fileName}-${file.lastModified || Date.now()}-${Math.random()}`;
        fileWithStatus.status = validation.isValid ? FileStatus.NEEDS_METADATA : FileStatus.INVALID;
        fileWithStatus.validation = validation;

        // Extract media metadata for auto-fill (only for valid files)
        if (validation.isValid) {
          try {
            // Import upload service dynamically to avoid circular imports
            const { uploadService } = await import('../../services/upload');
            const mediaMetadata = await uploadService.extractMediaMetadata(file);
            
            // Store metadata for auto-fill
            fileWithStatus.preview = {
              duration: mediaMetadata.duration,
              width: mediaMetadata.width,
              height: mediaMetadata.height
            };

            // Pre-fill some metadata
            const partialMetadata: Partial<AdMetadata> = {
              ratio: mediaMetadata.aspectRatio,
              duration: mediaMetadata.duration > 0 ? Math.round(mediaMetadata.duration * 10) / 10 : 0,
              language: 'EN', // Default language
              created_by: 'web_user', // Default creator
              watchers: []
            };

            fileWithStatus.metadata = partialMetadata as AdMetadata;
            
            console.log(`✅ Auto-filled metadata for ${fileName}: ratio=${mediaMetadata.aspectRatio}, duration=${mediaMetadata.duration}s`);
          } catch (error) {
            console.warn(`⚠️ Failed to extract media metadata for ${fileName}:`, error);
            // Continue without auto-fill if metadata extraction fails
            // File is still valid, user can manually enter metadata
          }
        }

        // Debug file size after processing - should be the same
        console.log('File size after processing:', fileWithStatus.name, fileWithStatus.size);
        console.log('File object type check:', fileWithStatus instanceof File ? 'Still a File ✅' : 'NOT a File ❌');

        newFiles.push(fileWithStatus);
      }

      // Update files list
      const updatedFiles = [...files, ...newFiles];
      onFilesChange(updatedFiles);
    },
    [files, onFilesChange, accept, maxSize]
  );

  

  const handleFileDelete = useCallback(
    (fileId: string) => {
      // Cleanup thumbnail URL if exists
      const url = thumbnails[fileId];
      if (url && url.startsWith('blob:')) {
        try { URL.revokeObjectURL(url); } catch {}
      }
      setThumbnails(prev => {
        const { [fileId]: _, ...rest } = prev;
        return rest;
      });
      onFileDelete(fileId);
    },
    [onFileDelete, thumbnails]
  );

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    accept: DROPZONE_ACCEPT,
    maxSize,
    multiple: true,
    disabled,
  });

  const formatFileSize = (bytes: number): string => {
    // Handle invalid inputs
    if (bytes === null || bytes === undefined || isNaN(bytes) || bytes < 0) {
      console.warn('formatFileSize received invalid input:', bytes);
      return '0 Bytes';
    }
    if (bytes === 0) {
      console.warn('formatFileSize received 0 bytes');
      return '0 Bytes';
    }
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getDropzoneStyles = () => {
    let borderColor = 'grey.300';
    let backgroundColor = 'background.paper';
    
    if (disabled) {
      borderColor = 'grey.200';
      backgroundColor = 'grey.50';
    } else if (isDragReject) {
      borderColor = 'error.main';
      backgroundColor = 'error.light';
    } else if (isDragActive) {
      borderColor = 'primary.main';
      backgroundColor = 'primary.light';
    }

    return {
      borderColor,
      backgroundColor,
      opacity: disabled ? 0.6 : 1,
    };
  };

  // Generate video thumbnail (first frame)
  const generateVideoThumbnail = (file: File): Promise<string | null> => {
    return new Promise((resolve) => {
      try {
        const video = document.createElement('video');
        const url = URL.createObjectURL(file);
        video.preload = 'metadata';
        video.src = url;
        video.muted = true;
        video.playsInline = true;

        const cleanup = () => {
          try { URL.revokeObjectURL(url); } catch {}
        };

        const capture = () => {
          const width = video.videoWidth || 320;
          const height = video.videoHeight || 180;
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (!ctx) { cleanup(); resolve(null); return; }
          ctx.drawImage(video, 0, 0, width, height);
          const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
          cleanup();
          resolve(dataUrl);
        };

        video.addEventListener('loadeddata', () => {
          try {
            // Seek a tiny bit to ensure we have a frame
            video.currentTime = Math.min(0.1, (video.duration || 0));
          } catch {
            capture();
          }
        });
        video.addEventListener('seeked', capture);
        video.addEventListener('error', () => { cleanup(); resolve(null); });
      } catch {
        resolve(null);
      }
    });
  };

  // Build thumbnails for any files missing one
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      for (const file of files) {
        if (thumbnails[file.id] || isHtmlUploadFile(file)) continue;
        const isImage = (file.type || '').startsWith('image/');
        if (isImage) {
          try {
            const url = URL.createObjectURL(file);
            if (!cancelled) setThumbnails(prev => ({ ...prev, [file.id]: url }));
          } catch {}
        } else {
          const thumb = await generateVideoThumbnail(file);
          if (!cancelled && thumb) setThumbnails(prev => ({ ...prev, [file.id]: thumb }));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [files, thumbnails]);

  // Cleanup all blob URLs on unmount
  React.useEffect(() => {
    return () => {
      Object.values(thumbnails).forEach(url => {
        if (url && url.startsWith('blob:')) {
          try { URL.revokeObjectURL(url); } catch {}
        }
      });
    };
  }, [thumbnails]);

  return (
    <Box>
      {/* Dropzone Area */}
      <Paper
        {...getRootProps()}
        sx={{
          border: 2,
          borderStyle: 'dashed',
          borderRadius: 2,
          p: 4,
          textAlign: 'center',
          cursor: disabled ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s ease-in-out',
          minHeight: 200,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          ...getDropzoneStyles(),
        }}
      >
        <input {...getInputProps()} />
        
        <CloudUploadIcon 
          sx={{ 
            fontSize: 64, 
            color: disabled ? 'grey.400' : isDragActive ? 'primary.main' : 'grey.500',
            mb: 2 
          }} 
        />
        
        <Typography variant="h6" gutterBottom>
          {isDragActive
            ? 'Drop your files here'
            : disabled
            ? 'Upload disabled'
            : 'Drag & drop videos, images, or HTML files here, or click to select'
          }
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Supported formats: {accept.join(', ')}
        </Typography>
        
        <Typography variant="body2" color="text.secondary">
          Maximum file size: {formatFileSize(maxSize)}
        </Typography>
      </Paper>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Files Grid - lean card UI (no progress bar) */}
      {files.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Grid container spacing={2}>
            {files.map((file) => {
              const isInvalid = file.status === FileStatus.INVALID;
              const previewUrl = thumbnails[file.id] || null;

              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={file.id}>
                  <Card sx={{ position: 'relative', overflow: 'hidden' }}>
                    <IconButton
                      onClick={(e) => { e.stopPropagation(); handleFileDelete(file.id); }}
                      size="small"
                      sx={{ position: 'absolute', top: 6, right: 6, bgcolor: 'rgba(0,0,0,0.4)', color: 'common.white', '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' } }}
                      aria-label="Remove file"
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>

                    {previewUrl ? (
                      <CardMedia component="img" image={previewUrl} alt={file.name} sx={{ height: 140, objectFit: 'cover' }} />
                    ) : (
                      <Box sx={{ height: 140, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.100' }}>
                        <VideoFileIcon sx={{ fontSize: 40, color: 'grey.500' }} />
                      </Box>
                    )}

                    <CardContent sx={{ pt: 1 }}>
                      <Tooltip title={file.metadata?.filename || file.name} placement="top">
                        <Typography variant="body2" noWrap>{file.metadata?.filename || file.name}</Typography>
                      </Tooltip>
                      <Typography variant="caption" color="text.secondary">
                        {formatFileSize(file.size)}{file.metadata?.ratio ? ` • ${file.metadata.ratio}` : ''}{file.metadata?.duration ? ` • ${file.metadata.duration}s` : ''}
                      </Typography>

                      {isInvalid && (
                        <Typography variant="caption" color="error.main">
                          {file.validation?.error || 'Invalid file'}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default DropZone; 